/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { CAN_USE_BEFORE_INPUT } from "@lexical/utils"
import { useEffect, useMemo, useState } from "react"

import { INITIAL_SETTINGS, isDevPlayground } from "./appSettings"
import { useSettings } from "./context/SettingsContext"
import Switch from "./ui/Switch"

export default function Settings({showButton}) {
  const windowLocation = window.location
  const {
    setOption,
    settings: {
      measureTypingPerf,
      isCollab,
      isRichText,
      isMaxLength,
      isCharLimit,
      isCharLimitUtf8,
      isAutocomplete,
      showTreeView,
      showNestedEditorTreeView,
      disableBeforeInput,
      showTableOfContents,
      shouldUseLexicalContextMenu,
      shouldPreserveNewLinesInMarkdown
    }
  } = useSettings()
  useEffect(() => {
    if (INITIAL_SETTINGS.disableBeforeInput && CAN_USE_BEFORE_INPUT) {
      console.error(
        `Legacy events are enabled (disableBeforeInput) but CAN_USE_BEFORE_INPUT is true`
      )
    }
  }, [])
  const [showSettings, setShowSettings] = useState(false)
  const [isSplitScreen, search] = useMemo(() => {
    const parentWindow = window.parent
    const _search = windowLocation.search
    const _isSplitScreen =
      parentWindow && parentWindow.location.pathname === "/split/"
    return [_isSplitScreen, _search]
  }, [windowLocation])

  return (
    <>
    {showButton ?  <button
        id="options-button"
        className={`editor-dev-button ${showSettings ? "active" : ""}`}
        onClick={() => setShowSettings(!showSettings)}
      /> : null}
      {showSettings ? (
        <div className="switches">
          {isRichText && isDevPlayground && (
            <Switch
              onClick={() => {
                setOption("isCollab", !isCollab)
                window.location.reload()
              }}
              checked={isCollab}
              text="Collaboration"
            />
          )}
          {isDevPlayground && (
            <Switch
              onClick={() => {
                if (isSplitScreen) {
                  window.parent.location.href = `/${search}`
                } else {
                  window.location.href = `/split/${search}`
                }
              }}
              checked={isSplitScreen}
              text="Split Screen"
            />
          )}
          <Switch
            onClick={() => setOption("measureTypingPerf", !measureTypingPerf)}
            checked={measureTypingPerf}
            text="Measure Perf"
          />
          <Switch
            onClick={() => setOption("showTreeView", !showTreeView)}
            checked={showTreeView}
            text="Debug View"
          />
          <Switch
            onClick={() =>
              setOption("showNestedEditorTreeView", !showNestedEditorTreeView)
            }
            checked={showNestedEditorTreeView}
            text="Nested Editors Debug View"
          />
          <Switch
            onClick={() => {
              setOption("isRichText", !isRichText)
              setOption("isCollab", false)
            }}
            checked={isRichText}
            text="Rich Text"
          />
          <Switch
            onClick={() => setOption("isCharLimit", !isCharLimit)}
            checked={isCharLimit}
            text="Char Limit"
          />
          <Switch
            onClick={() => setOption("isCharLimitUtf8", !isCharLimitUtf8)}
            checked={isCharLimitUtf8}
            text="Char Limit (UTF-8)"
          />
          <Switch
            onClick={() => setOption("isMaxLength", !isMaxLength)}
            checked={isMaxLength}
            text="Max Length"
          />
          <Switch
            onClick={() => setOption("isAutocomplete", !isAutocomplete)}
            checked={isAutocomplete}
            text="Autocomplete"
          />
          <Switch
            onClick={() => {
              setOption("disableBeforeInput", !disableBeforeInput)
              setTimeout(() => window.location.reload(), 500)
            }}
            checked={disableBeforeInput}
            text="Legacy Events"
          />
          <Switch
            onClick={() => {
              setOption("showTableOfContents", !showTableOfContents)
            }}
            checked={showTableOfContents}
            text="Table Of Contents"
          />
          <Switch
            onClick={() => {
              setOption(
                "shouldUseLexicalContextMenu",
                !shouldUseLexicalContextMenu
              )
            }}
            checked={shouldUseLexicalContextMenu}
            text="Use Lexical Context Menu"
          />
          <Switch
            onClick={() => {
              setOption(
                "shouldPreserveNewLinesInMarkdown",
                !shouldPreserveNewLinesInMarkdown
              )
            }}
            checked={shouldPreserveNewLinesInMarkdown}
            text="Preserve newlines in Markdown"
          />
        </div>
      ) : null}
    </>
  )
}
